import { createSlice } from '@reduxjs/toolkit';
import { slice as _slice, map } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';

// URLSearchParams
const initialArgs = {
  month: moment().format('YYYYMM')
};
const { month, vacationId } = proc.getQueryParams(initialArgs);

const initialCounters = {
  monthStats: {
    contractsAffected: 0,
    contractsTotal: 0,
    sitesActives: 0,
    sitesTotal: 0
  },
  anomaliesStats: {
    vacationsNotDid: 0,
    vacationsLate: 0,
    vacationGeolocFailed: 0
  }
};

const initialState = {
  isLoading: false,
  isPublished: true,
  error: false,
  month: month.toString(),
  vacationId,
  pickerDate: moment(month, 'YYYYMM').toDate(),
  // Manager datas
  counters: initialCounters,
  scheduleStats: [],
  // Resource datas
  shiftsCurrentMonth: [],
  shiftsNextMonth: [],
  holidays: [],
  // Client datas
  shiftsCurrentDay: [],
  hasUnpublishedActiveSites: false,
  update: false,
  unpublishedMonth: [],
  hideNextMonth: false
};

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    resetDashboard(state) {
      state.shiftsNextMonth = [];
      state.shiftsCurrentDay = [];
      state.shiftsCurrentMonth = [];
      state.counters = initialCounters;
      state.scheduleStats = [];
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getCounters(state, action) {
      // state.isLoading = false;
      state.counters.monthStats = action.payload;
    },

    getCounterAnomalies(state, action) {
      state.counters.anomaliesStats = action.payload;
    },

    getScheduleStats(state, action) {
      const { shifts } = action.payload;
      state.isLoading = false;
      state.scheduleStats = map(shifts, (el) => ({
        title: el.count,
        date: el.datetime_start
      }));
    },

    getResourceNextShifts(state, action) {
      state.isLoading = false;
      state.isPublished = action.payload.isPublished;
      state.shiftsCurrentMonth = action.payload.shiftsCurrentMonth;
      state.shiftsNextMonth = action.payload.shiftsNextMonth;
      state.unpublishedMonth = action.payload.unpublishedMonth;
      state.hideNextMonth = action.payload.hideNextMonth;
    },

    getClientShiftsDay(state, action) {
      state.isLoading = false;
      const shifts = action.payload.shifts.filter((el) => !el.unavailability_id);
      state.isPublished = action.payload.isPublished;
      state.hasUnpublishedActiveSites = action.payload.hasUnpublishedActiveSites ?? false;
      state.shiftsCurrentDay = shifts;
      state.holidays = action.payload.holidays;
    },

    setUpdate(state) {
      state.update = !state.update;
    },

    setDateDashboard(state, action) {
      // state.counters = initialCounters;
      state.scheduleStats = [];
      state.pickerDate = action.payload;
      state.month = moment(action.payload).format('YYYYMM');
    },

    clearVacationId(state) {
      state.vacationId = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { setDateDashboard, setUpdate, resetDashboard, clearVacationId } = slice.actions;

export function getCounters({ month, onlyAnomalies = null }) {
  return async (dispatch) => {
    // Early return si pas manager ou client
    const role = socket?.user?.role;
    if (!['manager', 'client'].includes(role)) {
      return;
    }

    // dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/dashboard/counters', {
        month,
        onlyAnomalies
      });

      if (onlyAnomalies) {
        dispatch(slice.actions.getCounterAnomalies(result));
      } else {
        dispatch(slice.actions.getCounters(result));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getScheduleStats({ month }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/services/stats', {
        month
      });
      dispatch(slice.actions.getScheduleStats(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getResourceNextShifts({ resourceId, month, limit = 15 }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    let hideNextMonth = false;
    try {
      let result = await socket.get('/schedules', {
        resourceId,
        dateStart: moment().format('YYYY-MM-DD'),
        dateEnd: moment(month, 'YYYYMM').endOf('month').format('YYYY-MM-DD'),
        currentdate: moment().format('YYYY-MM-DD HH:mm:ss')
      });

      const { isPublished, unpublishedMonth } = result;
      const shiftsCurrentMonth = result.shifts.slice(0, limit);

      let shiftsNextMonth = [];
      if (shiftsCurrentMonth.length < limit) {
        const nextMonth = moment(month, 'YYYYMM').add(1, 'months').format('YYYYMM');

        result = await socket.get('/schedules', {
          resourceId,
          month: nextMonth
        });

        let newNextMonth = result.shifts;

        // Cas publication manuelle, multiple contrat
        if (unpublishedMonth.length) {
          // On récupère les ids des contrats qui ne sont pas publiés sur le mois courant
          const nextMonthFilter = unpublishedMonth
            .filter((elem) => elem.month === parseInt(month, 10))
            .map((filter) => filter.contract_id);
          // On exclut les vacations qui auraient ces contract_id sur le mois suivant
          newNextMonth = newNextMonth.filter((shift) => !nextMonthFilter.includes(shift.contract_id));
        }
        // Limite le nombre de vacation sur le mois suivant en fonction du nombre de vacation restantes à afficher
        shiftsNextMonth = _slice(newNextMonth, 0, limit - shiftsCurrentMonth.length);
      } else {
        hideNextMonth = true;
      }

      dispatch(
        slice.actions.getResourceNextShifts({
          shiftsCurrentMonth,
          shiftsNextMonth,
          unpublishedMonth,
          hideNextMonth,
          isPublished
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getClientShiftsDay() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/services', {
        date: moment().format('YYYY-MM-DD')
      });
      dispatch(slice.actions.getClientShiftsDay(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
